import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import ConfirmationModal from "../../../../utils/ConfirmationModal";

import DashboardHeader from "../DashboardHeader";
import EMAResultsList from "./EMAResultsList";
import EMAMedicineDetailPanel from "./EMAMedicineDetailPanel";
import MigrateDropdown from "../MigrateDropdown";
import DownloadDropdown from "../DownloadDropdown";
import GraphComponent from "../../../copilotComponents/GraphComponent";

const EMA_SEARCH_STEPS = [
    { upTo: 10, label: "Initializing",              icon: "bi-gear",             desc: "Fetching search query" },
    { upTo: 20, label: "Checking Master Database",  icon: "bi-database-check",   desc: "Verifying synced EMA medicines" },
    { upTo: 50, label: "Classifying & Searching",   icon: "bi-search",           desc: "Matching medicines against EMA master database" },
    { upTo: 70, label: "Storing Results",           icon: "bi-cloud-download",   desc: "Saving matched medicines" },
    { upTo: 95, label: "Generating Charts",         icon: "bi-bar-chart",        desc: "Building status, timeline & therapeutic area charts" },
    { upTo: 100, label: "Saving Results",           icon: "bi-check2-circle",    desc: "Finalizing search results" },
];

const DiscoveryProgressBar = ({ progress }) => {
    const pct = Math.min(Math.max(progress || 0, 0), 100);
    const activeIdx = EMA_SEARCH_STEPS.findIndex(s => pct <= s.upTo);
    const currentIdx = activeIdx === -1 ? EMA_SEARCH_STEPS.length - 1 : activeIdx;

    return (
        <div className="vh-55 d-flex flex-column justify-content-center p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="discovery-step-icon-circle">
                        <i className={`bi ${EMA_SEARCH_STEPS[currentIdx].icon} ev-fs-14`} style={{ color: "#fff" }} />
                    </div>
                    <div>
                        <p className="ev-reg-p-dark ev-bold-med mb-0">
                            {EMA_SEARCH_STEPS[currentIdx].label}…
                        </p>
                        <p className="ev-small-p-dark text-muted mb-0">EMA search in progress</p>
                    </div>
                </div>
                <span className="ev-bold-med discovery-pct-label">{pct}%</span>
            </div>

            <div className="discovery-progress-track">
                <div
                    className="discovery-progress-fill"
                    style={{
                        width: `${pct}%`,
                        animation: pct < 100 ? "gradient-move 3s linear infinite" : "none",
                    }}
                />
            </div>

            <div className="row g-2">
                {EMA_SEARCH_STEPS.map((s, i) => {
                    const done = pct >= s.upTo;
                    const active = i === currentIdx;
                    const state = done ? "done" : active ? "active" : "pending";
                    return (
                        <div key={i} className="col-4 col-md-2">
                            <div
                                className="ev-card-2 discovery-step-card h-100 d-flex flex-column align-items-start"
                                style={{
                                    border: active ? "1px solid var(--color9)" : "1px solid transparent",
                                    opacity: done || active ? 1 : 0.45,
                                    transition: "opacity 0.4s, border-color 0.4s",
                                }}
                            >
                                <div className={`discovery-step-icon-box ${state}`}>
                                    {done
                                        ? <i className="bi bi-check-lg ev-fs-13" style={{ color: "#fff" }} />
                                        : <i className={`bi ${s.icon} ev-fs-13`} style={{
                                            color: active ? "var(--color1)" : "var(--color17)",
                                        }} />
                                    }
                                </div>
                                <p className="ev-small-p-dark ev-bold-med mb-0">{s.label}</p>
                                <p className="ev-small-p-dark mb-0 mt-1">{s.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const EMA_TABS = [
    { key: "medicines", label: "Medicines" },
    { key: "status",    label: "Status Chart" },
    { key: "timeline",  label: "Approval Timeline" },
    { key: "area",      label: "Therapeutic Area" },
];

const EMA_MIGRATE_PHASES = [
    { id: "ABSTRACT",  label: "Medicine Metadata (CSV)", shortLabel: "Metadata" },
    { id: "FULL_TEXT", label: "EPAR Documents (PDFs)",   shortLabel: "Documents" },
];

const EMADiscoveryDashboard = ({ search, onBack }) => {
    const navigate = useNavigate();

    // ── Status & processing ──
    const [searchProcessingStatus, setSearchProcessingStatus] = useState(
        search?.status || null
    );
    const [isProcessing, setIsProcessing]   = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isMigrating, setIsMigrating]     = useState(false);

    // ── Access control ──
    const [showDiscoveryAccess, setShowDiscoveryAccess] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState(
        (search?.discovery_access || []).map((u) => u.user_id)
    );

    // ── Results ──
    const [medicines, setMedicines]           = useState([]);
    const [selectedResult, setSelectedResult] = useState(null);
    const [selectedIds, setSelectedIds]       = useState([]);
    const [selectAll, setSelectAll]           = useState(false);

    // ── Tabs ──
    const [activeTab, setActiveTab] = useState("medicines");

    // ── Limit ──
    const [limit, setLimit] = useState(null);

    // ── Progress ──
    const [progressBar, setProgressBar] = useState(0);

    // ── Master DB sync status ──
    const [syncStatus, setSyncStatus] = useState(null);
    const [isSyncStatusLoading, setIsSyncStatusLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [showSyncConfirm, setShowSyncConfirm] = useState(false);

    // ── Graphs ──
    const [statusChartData, setStatusChartData]         = useState(null);
    const [timelineChartData, setTimelineChartData]     = useState(null);
    const [therapeuticAreaChartData, setTherapeuticAreaChartData] = useState(null);
    const [graphsLoading, setGraphsLoading] = useState(false);

    // ── Download / Migrate ──
    const [downloadType, setDownloadType] = useState("abstract"); // "abstract" -> csv, "full_text" -> documents
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedPhase, setSelectedPhase]     = useState(EMA_MIGRATE_PHASES[0]);
    const [metadataMigrationStatus, setMetadataMigrationStatus]   = useState(null);
    const [documentsMigrationStatus, setDocumentsMigrationStatus] = useState(null);

    // ── Popups ──
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const API_BASE_URL = "/pceapi/slr/discovery";

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    // ── Auto-load on mount ──
    useEffect(() => {
        handleRefresh();
        fetchGraphs();
        fetchSyncStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─────────────────────────────────────────────
    //  Handlers

    const handleRunSearch = async () => {
        if (searchProcessingStatus === "RUNNING" || isProcessing) return;

        setIsProcessing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Running EMA search...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/run`,
                {
                    discovery_id: search.id,
                    search_query: search.search_query,
                    search_limit: limit || null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSearchProcessingStatus("RUNNING");
                setPopupSuccessMessage("EMA search started successfully");
                await handleRefresh();
                await fetchGraphs();
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to run search");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Error running search");
        } finally {
            setIsProcessing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const fetchGraphs = async () => {
        if (!search?.id) return;
        setGraphsLoading(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/ema-graphs`,
                { discovery_id: search.id },
                { headers }
            );
            if (res.data?.status === "success") {
                const graphs = res.data.data?.graphs || {};
                setStatusChartData(graphs.status_chart ? { figure_dict: graphs.status_chart } : null);
                setTimelineChartData(graphs.approval_timeline ? { figure_dict: graphs.approval_timeline } : null);
                setTherapeuticAreaChartData(graphs.therapeutic_area ? { figure_dict: graphs.therapeutic_area } : null);
            }
        } catch (err) {
            console.error("Error fetching EMA graphs:", err);
        } finally {
            setGraphsLoading(false);
        }
    };

    const fetchSyncStatus = async () => {
        setIsSyncStatusLoading(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/ema-sync-status`,
                {},
                { headers }
            );
            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            if (res.data?.status === "success") {
                setSyncStatus(res.data.data || null);
            }
        } catch (err) {
            console.error("Error fetching EMA sync status:", err);
        } finally {
            setIsSyncStatusLoading(false);
        }
    };

    const handleSyncNow = async () => {
        if (isSyncing) return;
        setIsSyncing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Starting EMA master database sync...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/ema-sync`,
                {},
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage("EMA sync started. This runs in the background — refresh status after a few minutes.");
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to trigger EMA sync");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Error triggering EMA sync");
        } finally {
            setIsSyncing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleRefresh = async () => {
        setIsProcessing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Loading results...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/view-details`,
                { discovery_id: search.id },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                const payload = res.data.data;
                setSearchProcessingStatus(payload.processing_status || null);
                setProgressBar(payload.progress_bar || 0);
                setMetadataMigrationStatus(payload.ema_abstract_migration_status || null);
                setDocumentsMigrationStatus(payload.ema_migration_status || null);

                const meds = payload.medicines || [];
                setMedicines(meds);

                if (meds.length > 0) {
                    setSelectedResult((prev) =>
                        prev && meds.some((m) => m.ema_medicine_id === prev.ema_medicine_id)
                            ? prev
                            : meds[0]
                    );
                } else {
                    setSelectedResult(null);
                }

                const validIds = new Set(meds.map((m) => m.ema_medicine_id));
                setSelectedIds((prev) => prev.filter((id) => validIds.has(id)));
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to fetch results");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Error fetching results");
        } finally {
            setIsProcessing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleSaveAccess = async () => {
        setIsProcessing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Saving access...");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/save-user-access`,
                {
                    discovery_id: search.id,
                    user_ids: selectedUserIds,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage("Access updated successfully");
                setShowDiscoveryAccess(false);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to save access");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || "Error saving access");
        } finally {
            setIsProcessing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleDownload = async () => {
        if (selectedIds.length === 0) {
            setPopupErrorMessage("Please select at least one medicine to download.");
            return;
        }

        setIsDownloading(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Downloading...");

        try {
            const type = downloadType === "full_text" ? "documents" : "csv";
            const res = await axios.post(
                `${API_BASE_URL}/download-ema`,
                {
                    discovery_id: search.id,
                    selected_medicine_ids: selectedIds,
                    type,
                    document_types: null,
                },
                { headers, responseType: "blob" }
            );

            const blob = new Blob([res.data], {
                type: type === "csv" ? "text/csv" : "application/zip",
            });
            const url  = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href  = url;
            link.download = type === "csv"
                ? `ema_medicines_${search.id}.csv`
                : `ema_documents_${search.id}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setPopupSuccessMessage(`Downloaded ${selectedIds.length} medicines successfully.`);
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || "Failed to download.");
        } finally {
            setIsDownloading(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleMigrate = async ({ project, phase }) => {
        if (!project?.id) {
            setPopupErrorMessage("Please select a project to migrate to.");
            return;
        }

        if (selectedIds.length === 0) {
            setPopupErrorMessage("Please select at least one medicine to migrate.");
            return;
        }

        const isDocuments = phase?.id === "FULL_TEXT";

        setIsMigrating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Migrating ${selectedIds.length} medicines to ${project.name}...`);

        try {
            const endpoint = isDocuments ? "migrate-ema-documents" : "migrate-ema";
            const res = await axios.post(
                `${API_BASE_URL}/${endpoint}`,
                {
                    discovery_id: search.id,
                    selected_medicine_ids: selectedIds,
                    fabric_id: project.fabric_id ?? null,
                    thread_id: project.thread_id || null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage(
                    res.data.message || `Successfully started migration for ${selectedIds.length} medicines.`
                );
            } else {
                setPopupErrorMessage(res.data?.message || "Migration failed.");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || "Failed to migrate.");
        } finally {
            setIsMigrating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleUserSelection = (userId) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        setSelectedIds(checked ? medicines.map((m) => m.ema_medicine_id) : []);
    };

    const handleSelectOne = (id, checked) => {
        setSelectedIds((prev) =>
            checked ? [...prev, id] : prev.filter((x) => x !== id)
        );
        if (!checked) setSelectAll(false);
    };

    // ─────────────────────────────────────────────
    //  Render
    // ─────────────────────────────────────────────

    return (
        <div className="mx-1 pb-4">

            {/* ── Top header ── */}
            <DashboardHeader
                searchProcessingStatus={searchProcessingStatus}
                isProcessing={isProcessing}
                showDiscoveryAccess={showDiscoveryAccess}
                setShowDiscoveryAccess={setShowDiscoveryAccess}
                selectedUserIds={selectedUserIds}
                onUserSelection={handleUserSelection}
                onSaveAccess={handleSaveAccess}
                ownerEmail={search?.owner_email || search?.owner || null}
                apiBaseUrl={API_BASE_URL}
                onRunSearch={handleRunSearch}
                onRefresh={handleRefresh}
                onBack={onBack}
                label="Discovery Dashboard"
                extraActions={
                    <input
                        type="number"
                        min={1}
                        className="formField5 ev-btn-small w-10 ev-bold-med ev-radius-xsmall"
                        placeholder="Limit"
                        value={limit ?? ""}
                        onChange={(e) => {
                            const val = e.target.value;
                            setLimit(val ? Number(val) : null);
                        }}
                    />
                }
            />

            {/* ── Search term card + EMA Master Database sync strip (same row) ── */}
            <div className="row g-2 mb-2">
                <div className="col-6">
                    <div className="ev-card-6 p-3 ev-radius-xsmall h-100">
                        <p className="ev-small-p-dark mb-0">
                            {search?.search_query || "—"}
                        </p>
                    </div>
                </div>

                <div className="col-6">
                    <div className="ev-card-2 p-3 ev-radius-xsmall h-100 d-flex align-items-center justify-content-between">
                        <div>
                            <p className="ev-small-p-dark ev-bold-med mb-1">EMA Master Database</p>
                            {isSyncStatusLoading ? (
                                <p className="ev-xsmall-p-dark text-muted mb-0">Loading sync status...</p>
                            ) : !syncStatus || syncStatus.never_synced ? (
                                <p className="ev-xsmall-p-dark text-muted mb-0">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    Never synced. Searches will fail.
                                </p>
                            ) : (
                                <p className="ev-xsmall-p-dark text-muted mb-0">
                                    {syncStatus.synced_at ? new Date(syncStatus.synced_at).toLocaleString() : "—"}
                                    {"  |  "}{syncStatus.medicines_count ?? 0} medicines
                                    {"  |  "}{syncStatus.epar_docs_count ?? 0} documents
                                </p>
                            )}
                        </div>
                        <div className="d-flex flex-column gap-1">
                            <button
                                type="button"
                                className="ev-btn-lightblue-fill-round ev-btn-xsmall d-flex align-items-center justify-content-center gap-1"
                                onClick={fetchSyncStatus}
                                disabled={isSyncStatusLoading}
                            >
                                <i className="bi bi-arrow-clockwise" /> Refresh Status
                            </button>
                            <button
                                type="button"
                                className="ev-btn-blue-fill-round ev-btn-xsmall d-flex align-items-center justify-content-center gap-1"
                                onClick={() => setShowSyncConfirm(true)}
                                disabled={isSyncing}
                            >
                                {isSyncing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Syncing...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cloud-arrow-up" /> Sync Now
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Tabs + toolbar ── */}
            <div className="ev-card-4 mx-1 px-3 py-2">
                <div className="d-flex justify-content-between align-items-center gap-1 mb-2">

                    {/* Tabs */}
                    <div className="ev-message-card6 ev-radius-xsmall p-1 d-flex gap-2">
                        {EMA_TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={
                                    activeTab === tab.key
                                        ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med"
                                        : "ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-bold-med"
                                }
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Toolbar */}
                    <div className="d-flex align-items-center flex-shrink-0 gap-2">
                        <DownloadDropdown
                            emaMode={true}
                            downloadType={downloadType}
                            onTypeChange={setDownloadType}
                            onDownload={handleDownload}
                            disabled={selectedIds.length === 0 || isDownloading}
                            isLoading={isDownloading}
                        />
                        <MigrateDropdown
                            selectedProject={selectedProject}
                            onSelectProject={setSelectedProject}
                            phases={EMA_MIGRATE_PHASES}
                            selectedPhase={selectedPhase}
                            onSelectPhase={setSelectedPhase}
                            abstractMigrationStatus={metadataMigrationStatus}
                            fulltextMigrationStatus={documentsMigrationStatus}
                            showPhase={true}
                            dropdownAlign="end"
                            onMigrate={handleMigrate}
                            disabled={selectedIds.length === 0 || isMigrating}
                            isLoading={isMigrating}
                        />
                    </div>
                </div>

                {/* ── Tab content ── */}
                <div className="row">

                    {/* Status Chart tab */}
                    {activeTab === "status" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading status chart...</p>
                                </div>
                            ) : statusChartData ? (
                                <GraphComponent
                                    graphData={statusChartData}
                                    isLoading={false}
                                    contentId="ema-status"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Status chart not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Approval Timeline tab */}
                    {activeTab === "timeline" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading approval timeline...</p>
                                </div>
                            ) : timelineChartData ? (
                                <GraphComponent
                                    graphData={timelineChartData}
                                    isLoading={false}
                                    contentId="ema-timeline"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Approval timeline not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Therapeutic Area tab */}
                    {activeTab === "area" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading therapeutic area chart...</p>
                                </div>
                            ) : therapeuticAreaChartData ? (
                                <GraphComponent
                                    graphData={therapeuticAreaChartData}
                                    isLoading={false}
                                    contentId="ema-area"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Therapeutic area chart not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Medicines tab — progress bar when running */}
                    {activeTab === "medicines" && searchProcessingStatus === "RUNNING" && (
                        <div className="col-12">
                            <DiscoveryProgressBar progress={progressBar} />
                        </div>
                    )}

                    {/* Medicines tab — results */}
                    {activeTab === "medicines" && searchProcessingStatus !== "RUNNING" && medicines.length > 0 && (
                        <>
                            <div className="col-5 pe-1">
                                <EMAResultsList
                                    medicines={medicines}
                                    selectedResult={selectedResult}
                                    onSelectResult={setSelectedResult}
                                    selectedIds={selectedIds}
                                    selectAll={selectAll}
                                    onSelectAll={handleSelectAll}
                                    onSelectOne={handleSelectOne}
                                />
                            </div>
                            <div className="col-7 ps-1">
                                <EMAMedicineDetailPanel medicine={selectedResult} />
                            </div>
                        </>
                    )}

                    {/* Medicines tab — empty state */}
                    {activeTab === "medicines" && searchProcessingStatus !== "RUNNING" && medicines.length === 0 && (
                        <div className="col-12">
                            <div className="d-flex flex-column align-items-center justify-content-center vh-55 py-5">
                                <i className="bi bi-capsule ev-fs-50 text-muted mb-3"></i>
                                <p className="ev-reg-p-dark ev-bold-med mb-1">No EMA Medicines Found</p>
                                <p className="ev-small-p-dark text-muted text-center mb-0">
                                    Try adjusting your search or run a new search to find EMA medicines.
                                </p>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* ── Popups ── */}
            {isProcessingPopupVisible && (
                <ProcessingPopup message={processingMessage || "Processing..."} />
            )}

            {popupErrorMessage && (
                <ErrorPopup
                    message={popupErrorMessage}
                    onClose={() => setPopupErrorMessage("")}
                />
            )}

            {popupSuccessMessage && (
                <SuccessPopup
                    message={popupSuccessMessage}
                    onClose={() => setPopupSuccessMessage("")}
                />
            )}

            <ConfirmationModal
                show={showSyncConfirm}
                onClose={() => setShowSyncConfirm(false)}
                onConfirm={() => {
                    setShowSyncConfirm(false);
                    handleSyncNow();
                }}
                title="Sync EMA Master Database"
                message="This will trigger a fresh sync of the EMA master database in the background. It may take a few minutes to complete. Do you want to continue?"
                confirmButtonText="Sync Now"
                confirmButtonClass="ev-btn-blue-fill-round"
            />
        </div>
    );
};

export default EMADiscoveryDashboard;